<template>
  <v-dialog v-model="active" max-width="1200px" persistent class="l-dialog">
    <template v-slot:activator="{ on }">
      <slot name="activator" v-bind="{ on: on }" />
    </template>
    <v-card class="l-card" height="100%">
      <v-container fluid class="background pa-0 l-card__title__container">
        <v-row align="center" class="ma-0 l-card__title__row">
          <v-col cols="12" class="pa-4">
            <span class="l-card__title__text">Adicionar lote a Planta</span>
            <span class="l-card__title__text font-weight-bold pl-1">SIF {{ nrRegistroSif }}</span>
          </v-col>
          <v-btn
            absolute
            right
            top
            icon
            x-small
            color="#E5E8E7"
            @click="active = !active"
            :disabled="isAdding"
          >
            <v-icon>fa fa-times</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid class="pa-0 ma-0 l-card__content__container_c">
        <v-row
          align="center"
          class="ma-0 l-card__filter__row"
          md-gutter
          v-on:keyup.enter="filterLotsData"
        >
          <v-col>
            <v-text-field
              dense
              label="Produtor"
              placeholder="Produtor"
              outlined
              hide-details
              clearable
              v-model="filters.nmProdutor"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              dense
              label="Cidade"
              placeholder="Cidade"
              :items="cities"
              item-text="nmMunicipio"
              item-value="idMunicipio"
              outlined
              hide-details
              clearable
              v-model="filters.cdMunicipio"
            />
          </v-col>
          <v-col>
            <v-select
              dense
              label="Tipo UEP"
              placeholder="Tipo UEP"
              :items="tiposUep"
              outlined
              clearable
              hide-details
              v-model="filters.csTipoUep"
              :menu-props="{
                offsetY: true,
                bottom: true
              }"
            />
          </v-col>
          <v-col>
            <v-text-field
              dense
              label="Dias alojados de"
              placeholder="Dias alojados de"
              outlined
              hide-details
              clearable
              v-only-int
              v-model="filters.qtDiasAlojadoStart"
            />
          </v-col>
          <v-col>
            <v-text-field
              dense
              label="Dias alojados até"
              placeholder="Dias alojados até"
              outlined
              hide-details
              v-only-int
              clearable
              v-model="filters.qtDiasAlojadoEnd"
            />
          </v-col>
          <v-col>
            <v-row align="center" class="ma-0">
              <v-btn-toggle dense class="white--text">
                <v-btn color="primary" class="white--text" @click="clearFilters">Limpar</v-btn>
                <v-btn color="#2BC1B6" class="white--text mx-1" @click="filterLotsData">Buscar</v-btn>
              </v-btn-toggle>
            </v-row>
          </v-col>
        </v-row>
        <v-divider />
        <v-row v-if="availableLots.length === 0" class="ma-0">
          <v-col cols="12" class="pa-4">
            <span>Nenhum registro encontrado</span>
          </v-col>
        </v-row>
        <v-row class="ma-0 l-card__content__row">
          <v-virtual-scroll
            bench="2"
            :items="availableLots | chunk(5)"
            item-height="340"
            min-height="400"
            max-height="680"
            width="100%"
            ref="lotsContent"
          >
            <template v-slot="{ item }">
              <v-row class="ma-0">
                <v-col v-for="lot in item" :key="lot['idLoteProducao']" class="five-cols">
                  <lot-card
                    v-model="selectedLots[lot.idLoteProducao]"
                    :lot="lot"
                    :cd-planta-abate="cdPlantaAbate"
                  />
                </v-col>
              </v-row>
            </template>
          </v-virtual-scroll>
          <v-overlay absolute :value="loading">
            <v-row align="center" justify="center" class="ma-0">
              <v-progress-circular indeterminate size="42" color="secondary" />
            </v-row>
          </v-overlay>
        </v-row>
      </v-container>
      <v-container fluid class="l-card__content__action white pa-0 elevation-5">
        <v-row class="ma-0 pa-2 px-4">
          <v-btn
            depressed
            class="blue--text"
            @click="active = !active"
            :disabled="isAdding"
          >Cancelar</v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="#2BC1B6"
            class="white--text"
            @click="onAddClick"
            :loading="isAdding"
            :disabled="isAddDisabled"
          >Adicionar</v-btn>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import services from "@/http/services";
import moment from "moment";
import LotCard from "@/modules/programacaoAbate/components/LotTab/LotCard";
import { SlaughterProgrammingEventBus } from "@/modules/programacaoAbate/events";

export default {
  name: "LotListDialog",
  components: { LotCard },
  props: {
    cdProgramacaoAbate: {
      type: Number,
    },
    cdPlantaAbate: {
      type: Number,
    },
    nrRegistroSif: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    active: false,
    selectedLots: {},
    currPage: 1,
    loading: false,
    isAdding: false,

    meta: null,
    availableLots: [],

    filters: {
      nmProdutor: null,
      cdMunicipio: null,
      csTipoUep: null,
      qtDiasAlojadoStart: null,
      qtDiasAlojadoEnd: null,
    },
  }),
  methods: {
    async onScroll(el) {
      const atBottom = el.offsetHeight + el.scrollTop === el.scrollHeight;
      if (atBottom) {
        if (this.meta) {
          if (!this.meta["has_next"]) return;
        }
        await this.getPaginatedLotsData();
      }
    },
    clearFilters() {
      this.filters = {
        nmProdutor: null,
        cdMunicipio: null,
        csTipoUep: null,
        qtDiasAlojadoStart: null,
        qtDiasAlojadoEnd: null,
      };
      this.clearData();
      this.filterLotsData();
    },
    filterLotsData() {
      this.clearData();
      this.loading = true;
      let params = {};

      params.per_page = 20;
      params.page = this.currPage;

      if (this.filters.nmProdutor) params.nmProdutor = this.filters.nmProdutor;
      if (this.filters.cdMunicipio)
        params.cdMunicipio = this.filters.cdMunicipio;
      if (this.filters.csTipoUep) params.csTipoUep = this.filters.csTipoUep;
      if (this.filters.qtDiasAlojadoStart)
        params.qtDiasAlojadoStart = this.filters.qtDiasAlojadoStart;
      if (this.filters.qtDiasAlojadoEnd)
        params.qtDiasAlojadoEnd = this.filters.qtDiasAlojadoEnd;

      return services.programacaoAbate
        .loadBatchsAvaliable({
          cdProgramacaoAbate: this.cdProgramacaoAbate,
          cdPlantaAbate: this.cdPlantaAbate,
          ...params,
        })
        .then((resp) => {
          if (!resp.data) return;
          if (resp.data.length === 0) return;

          const data = resp.data[0].data;
          const meta = resp.data[0].meta;
          if (!data) return;

          let availableLots = data.map((entry) => {
            const foundCity = this.cities.find(
              (i) => i["idMunicipio"] === entry.jsonEstabelecimento.cdMunicipio
            );
            return {
              ...entry,
              nmCity: foundCity ? foundCity.nmMunicipio : null,
            };
          });

          availableLots = [...(this.availableLots || []), ...availableLots];

          this.availableLots = availableLots;
          this.meta = meta;
        })
        .finally(() => (this.loading = false));
    },
    async getPaginatedLotsData() {
      this.loading = true;
      let params = {};

      // Filtros
      params.per_page = 20;
      params.page = this.currPage;

      if (this.filters.nmProdutor) params.nmProdutor = this.filters.nmProdutor;
      if (this.filters.cdMunicipio)
        params.cdMunicipio = this.filters.cdMunicipio;
      if (this.filters.csTipoUep) params.csTipoUep = this.filters.csTipoUep;
      if (this.filters.qtDiasAlojadoStart)
        params.qtDiasAlojadoStart = this.filters.qtDiasAlojadoStart;
      if (this.filters.qtDiasAlojadoEnd)
        params.qtDiasAlojadoEnd = this.filters.qtDiasAlojadoEnd;

      return services.programacaoAbate
        .loadBatchsAvaliable({
          cdProgramacaoAbate: this.cdProgramacaoAbate,
          cdPlantaAbate: this.cdPlantaAbate,
          ...params,
        })
        .then((resp) => {
          if (!resp.data) return;
          if (resp.data.length === 0) return;

          const data = resp.data[0].data;
          const meta = resp.data[0].meta;
          if (!data) return;

          let availableLots = data.map((entry) => {
            const foundCity = this.cities.find(
              (i) => i["idMunicipio"] === entry.jsonEstabelecimento.cdMunicipio
            );
            return {
              ...entry,
              nmCity: foundCity ? foundCity.nmMunicipio : null,
            };
          });

          availableLots = [...(this.availableLots || []), ...availableLots];

          this.availableLots = availableLots;
          this.meta = meta;
          this.currPage++;
        })
        .finally(() => (this.loading = false));
    },
    onAddClick() {
      this.isAdding = true;
      const items = Object.keys(this.selectedLots).map((key) => {
        const payload = [];
        this.selectedLots[key].forEach((val) => {
          payload.push({
            cd_programacao_abate: this.cdProgramacaoAbate,
            cd_lote_producao: key,
            cd_planta_abate: this.cdPlantaAbate,
            st_dia_alocado: val["stDiaAlocado"],
            dt_alocado: moment(val["dtAlocado"], "DD/MM/YYYY").format(
              "YYYY-MM-DD"
            ),
            nr_peso_dia: val["nrPesoDia"],
            qt_animais_alocados: 0,
          });
        });
        return payload;
      });
      return services.programacaoAbate
        .insertSlaughterProgrammingAllocatedLots(items.flat())
        .then(() => {
          this.$store.commit("setSnack", {
            type: "success",
            msg: "Lotes alocados com sucesso",
          });
          this.active = false;
          this.clearData();
          this.getPaginatedLotsData();
          this.ActionLoadBatchsAllocated(this.cdProgramacaoAbate);
        })
        .finally(() => {
          this.isAdding = false;
        });
    },
    clearData() {
      Object.keys(this.selectedLots).forEach(
        (key) => (this.selectedLots[key] = [])
      );
      this.loading = false;
      this.isAdding = false;
      this.availableLots = [];
      this.meta = null;
      this.currPage = 1;
    },
    ...mapActions("programacaoAbate", [
      "ActionLoadBatchsAllocated",
      "actionChangeDialogLotList",
    ]),
  },
  filters: {
    chunk(array, length) {
      let totalChunks = [];
      let chunkLength = parseInt(length, 10);

      if (chunkLength <= 0) {
        return array;
      }

      // Loop through the data and divide it into chunks
      for (let i = 0; i < array.length; i += chunkLength) {
        totalChunks.push(array.slice(i, i + chunkLength));
      }

      // Return the chunks of data
      return totalChunks;
    },
  },
  computed: {
    tiposUep() {
      return ["TR", "WF", "CR"];
    },
    isAddDisabled() {
      if (!this.selectedLots) return true;
      const items = Object.keys(this.selectedLots).map((key) => {
        const payload = [];
        this.selectedLots[key].forEach((val) => {
          payload.push({
            cd_programacao_abate: this.cdProgramacaoAbate,
            cd_lote_producao: key,
            cd_planta_abate: this.cdPlantaAbate,
            st_dia_alocado: val["stDiaAlocado"],
            dt_alocado: moment(val["dtAlocado"], "DD/MM/YYYY").format(
              "YYYY-MM-DD"
            ),
            nr_peso_dia: val["nrPesoDia"],
            qt_animais_alocados: 0,
          });
        });
        return payload;
      });
      return items.flat().length === 0;
    },
    ...mapState("programacaoAbate", ["cities"]),
  },
  mounted() {
    SlaughterProgrammingEventBus.$on(
      "reset-lots-list-dialog",
      this.clearFilters
    );
  },
  watch: {
    active(value) {
      if (value) {
        if (!this.meta) this.getPaginatedLotsData();
        this.$nextTick().then(() => {
          const lotsContent = this.$refs["lotsContent"].$el;
          lotsContent.addEventListener("scroll", () =>
            this.onScroll(lotsContent)
          );
        });
      } else {
        const lotsContent = this.$refs["lotsContent"].$el;
        lotsContent.removeEventListener("scroll", () =>
          this.onScroll(lotsContent)
        );
      }
      this.actionChangeDialogLotList(value);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .v-dialog {
  overflow-y: hidden !important;
  height: 90% !important;
  position: relative !important;
  z-index: 99999999;
}

.l-card {
  position: relative !important;
}

.l-card__title__container {
  height: 54px;
  position: relative;
}

.l-card__title__row {
  height: 54px;
  position: relative;
}

.l-card__title__text {
  font: Regular 16px/21px Roboto;
  letter-spacing: 0;
  color: #e5e8e7;
}

.l-card__content__container_c {
  background: #f7f7f7 0 0 no-repeat padding-box;
  opacity: 1;
  position: relative;
}

.l-card__content__action {
  position: absolute !important;
  z-index: 99999999 !important;
  bottom: 0 !important;
  left: 0 !important;
}

.five-cols {
  flex: 0 1 20% !important;
  max-width: 20% !important;
}
</style>
